import { AsyncPipe, KeyValue } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { filter, map, Observable, Subject, take, takeUntil } from 'rxjs';
import { CommonFormattingModule } from '@capital-access/common/formatting';
import { CommonLocalizationModule, LocalizationService, LocalizePipe } from '@capital-access/common/localization';
import { AuthorizeImpersonationRequest, getUser, ImpersonationRepository } from '@capital-access/common/user';
import {
  Alert,
  ChipSize,
  DrawerPosition,
  FireflyAlertsModule,
  FireflyChipModule,
  FireflyDrawerContent,
  FireflyDrawerModule,
  FireflyDrawerService,
  FireflyDropdownModule,
  FireflyListModule,
  LoadingType,
  NotificationService,
  NotificationType
} from '@capital-access/firefly/components';
import { AccessScopes, ImpersonationDuration, SupportRequestStatus } from '../../../models/impersonation-enums';
import { ImpersonationTermsComponent } from './impersonation-terms.component';
import { ImpersonationTermsDrawerComponent } from './impersonation-terms-drawer.component';
import { ImpersonationLocalizationKeys } from './pipes/impersonation-duration-localization.pipe';

@Component({
  templateUrl: './authorize-impersonation.component.html',
  styleUrls: ['./authorize-impersonation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [LocalizePipe],
  imports: [
    AsyncPipe,
    CommonLocalizationModule,
    FireflyDrawerModule,
    FireflyChipModule,
    ReactiveFormsModule,
    FireflyAlertsModule,
    CommonFormattingModule,
    ImpersonationTermsComponent,
    FireflyDropdownModule,
    FireflyListModule
  ]
})
export class AuthorizeImpersonationComponent
  extends FireflyDrawerContent<void>
  implements OnInit, AfterViewInit, OnDestroy
{
  ChipSize = ChipSize;
  readonly MAX_LENGTH = 5000;
  private destroyed$ = new Subject<void>();
  readonly localizationScope = 'navigation';

  impersonationForm!: FormGroup;
  isValidationFormErrorMessageShown = false;
  alertType = Alert;
  expirationDate: DateTime | null = null;
  selectedSupportRequestStatus: SupportRequestStatus = SupportRequestStatus.No;
  accessScopeItems = [{ id: AccessScopes.CapitalAccess, text: 'Capital Access' }];
  selectedAccessScope: AccessScopes = AccessScopes.CapitalAccess;
  impersonationDuration = ImpersonationDuration;
  supportRequestStatusItems$!: Observable<Array<{ id: SupportRequestStatus; text: string }>>;
  selectedImpersonationDuration: string | null = null;
  impersonationDurationDisplayText: string = '';
  impersonationDurationItems$!: Observable<Array<{ value: string; displayText: string }>>;
  private formBuilder = inject(FormBuilder);
  private store = inject(Store);
  private cdr = inject(ChangeDetectorRef);

  constructor(
    private localizationService: LocalizationService,
    private impersonationRepository: ImpersonationRepository,
    private notificationService: NotificationService,
    private drawerService: FireflyDrawerService
  ) {
    super();
  }

  ngOnInit(): void {
    this.initForm();
    this.drawerService.setForm(this.impersonationForm);
    this.supportRequestStatusItems$ = this.localizationService.getLocalization(this.localizationScope).pipe(
      map(translations =>
        Object.values(SupportRequestStatus).map(value => ({
          id: value,
          text: translations[value.toLowerCase()] || value
        }))
      )
    );

    this.impersonationDurationItems$ = this.localizationService.getLocalization(this.localizationScope).pipe(
      map(translations => {
        const durations = Object.entries(this.impersonationDuration);
        return durations
          .sort((a, b) => Number(a[1]) - Number(b[1]))
          .map(([key, value]) => {
            const localizationKey = this.getImpersonationDurationLocalizationKey(key);
            return {
              value: value,
              displayText: translations[localizationKey] || key
            };
          });
      })
    );
  }

  private getImpersonationDurationLocalizationKey(key: string): string {
    return ImpersonationLocalizationKeys[key as keyof typeof ImpersonationLocalizationKeys] || key.toLowerCase();
  }

  ngAfterViewInit(): void {
    this.drawerInstanceRef
      ?.visibilityChange()
      .pipe(filter(Boolean), takeUntil(this.destroyed$))
      .subscribe(() => {
        this.drawerInstanceRef.setConfirmationModalTrigger(true);
      });
    this.drawerInstanceRef
      ?.modalLeaveTriggered()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.impersonationForm.markAsPristine();
      });
  }

  get notes() {
    return this.impersonationForm.get('notes');
  }

  get maxLengthError() {
    return this.notes?.errors?.['maxlength'];
  }

  get invalid() {
    return Boolean(this.maxLengthError) && (this.notes?.touched || this.notes?.dirty);
  }

  get impersonationWindowControl() {
    return this.impersonationForm.get('impersonationWindow');
  }

  get showImpersonationWindowError() {
    return this.impersonationWindowControl?.touched && this.impersonationWindowControl?.hasError('required');
  }

  initForm(): void {
    this.impersonationForm = this.formBuilder.group({
      supportRequestStatus: [this.selectedSupportRequestStatus, Validators.required],
      impersonationWindow: [null, Validators.required],
      accessScope: [this.selectedAccessScope],
      notes: ['', [Validators.maxLength(this.MAX_LENGTH)]]
    });
  }

  onImpersonationDurationChange(item: { value: string; displayText: string }): void {
    const numericDuration = Number(item.value);
    this.selectedImpersonationDuration = item.value;
    this.impersonationDurationDisplayText = item.displayText;
    this.expirationDate = DateTime.now().plus({ days: numericDuration });
    this.impersonationForm.patchValue({
      impersonationWindow: item.value
    });
    this.impersonationForm.markAsDirty();
  }

  onSupportStatusChange(item: Record<string, unknown>): void {
    this.selectedSupportRequestStatus = item['id'] as SupportRequestStatus;
    this.impersonationForm.patchValue({
      supportRequestStatus: this.selectedSupportRequestStatus
    });
    this.impersonationForm.markAsDirty();
  }

  onSubmit(): void {
    if (!this.impersonationForm.valid) {
      this.handleInvalidForm();
      return;
    }
    this.drawerService.setDrawerLoadingState(LoadingType.Overlay);
    this.setValidationFormErrorMessage(false);
    this.processAuthorization();
  }

  setValidationFormErrorMessage(value: boolean) {
    this.isValidationFormErrorMessageShown = value;
  }

  keyOrder = (a: KeyValue<string, string>, b: KeyValue<string, string>): number => {
    return Number(a.value) - Number(b.value);
  };

  showImpersonationDisclosure() {
    this.drawerInstanceRef.setConfirmationModalTrigger(false);
    this.localizationService
      .getLocalization(this.localizationScope)
      .pipe(
        map(({ disclosure }) => {
          return this.drawerService.openDrawer(
            {
              title: disclosure,
              position: DrawerPosition.Right,
              addBackButton: true
            },
            ImpersonationTermsDrawerComponent
          );
        }),
        take(1)
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private handleInvalidForm(): void {
    this.impersonationForm.markAllAsTouched();
    this.setValidationFormErrorMessage(true);
    this.cdr.markForCheck();
  }

  private processAuthorization(): void {
    this.store
      .select(getUser)
      .pipe(take(1))
      .subscribe(currentUser => {
        if (!currentUser?.id) {
          return;
        }

        const request = this.buildAuthorizationRequest(currentUser.id);
        this.submitAuthorizationRequest(request);
      });
  }

  private buildAuthorizationRequest(userId: number): AuthorizeImpersonationRequest {
    const formValue = this.impersonationForm.getRawValue();

    return {
      userId,
      isSupportRequestCreated: formValue.supportRequestStatus === SupportRequestStatus.Yes,
      accessScope: [formValue.accessScope],
      accessDurationDays: Number(formValue.impersonationWindow),
      notes: formValue.notes || '',
      authorizedByUserId: userId
    };
  }

  private submitAuthorizationRequest(request: AuthorizeImpersonationRequest): void {
    this.impersonationRepository.authorizeImpersonation(request).subscribe({
      next: () => {
        this.showNotification('impersonationAuthorizeSuccess', 'success');
        this.drawerService.setDrawerLoadingState();
      },
      error: () => {
        this.showNotification('impersonationAuthorizeFailed', 'danger');
        this.drawerService.setDrawerLoadingState();
      }
    });
  }

  private showNotification(localizationKey: string, type: NotificationType): void {
    this.localizationService
      .localize(localizationKey, undefined, { scope: this.localizationScope })
      .pipe(take(1))
      .subscribe(msg => {
        this.drawerService.closeDrawersWithoutConfirmation();
        this.notificationService.notify(msg, { type });
      });
  }
}
